import { useCallback, useMemo } from 'react';
import {
  MatrixCategory,
  MatrixCriterion,
  MatrixCriterionValue,
  MatrixFeature,
  MatrixFeatureHome,
  NewMatrixCriterion
} from '../../../types/advise';
import {
  createAdviseMatrixCategoryFeature,
  createAdviseMatrixCriterion,
  createAdviseMatrixCriterionFeature,
  deleteAdviseMatrixCriterion,
  deleteAdviseMatrixFeature,
  fetchAdviseFunctionalityMatrix,
  updateAdviseMatrixCriterion,
  updateAdviseMatrixFeature
} from '../../../Api/Advise/Engagements';
import { CallResult } from '../../../Api/callExternalApi';
import { isNotFoundRefusal, refusalMessage } from '../../../utils/refusalMessage';
import { EngagementFunctionalityMatrix } from '../../Compounds/Advise/SideBySideView';
import { usePendingSaves } from './usePendingSaves';
import { useLoadAfterSaves } from './useLoadAfterSaves';

type PendingSaves = ReturnType<typeof usePendingSaves>;

const withCriterion = (criteria: MatrixCriterion[], id: string, value: MatrixCriterionValue) =>
  criteria.map((criterion) =>
    criterion.id === id
      ? { ...criterion, selected: value.selected, weight: value.selected ? value.weight : null }
      : criterion
  );

const withFeature = (criteria: MatrixCriterion[], id: string, selected: boolean) =>
  criteria.map((criterion) => ({
    ...criterion,
    categories: criterion.categories.map((category) => ({
      ...category,
      features: category.features.map((feature) => (feature.id === id ? { ...feature, selected } : feature))
    }))
  }));

// Each add leaves what's there as it is, so an add a load has already shown is never shown twice.
const withCriterionAdded = (criteria: MatrixCriterion[], added: MatrixCriterion) =>
  criteria.some((criterion) => criterion.id === added.id) ? criteria : [...criteria, added];

const withCategoryFeature = (criteria: MatrixCriterion[], categoryId: string, added: MatrixFeature) =>
  criteria.map((criterion) => ({
    ...criterion,
    categories: criterion.categories.map((category) =>
      category.id === categoryId && !category.features.some((feature) => feature.id === added.id)
        ? { ...category, features: [...category.features, added] }
        : category
    )
  }));

const withCriterionCategory = (criteria: MatrixCriterion[], criterionId: string, added: MatrixCategory) =>
  criteria.map((criterion) =>
    criterion.id === criterionId && !criterion.categories.some((category) => category.id === added.id)
      ? { ...criterion, categories: [...criterion.categories, added] }
      : criterion
  );

// The API removes a category made for a criterion with none along with its last feature.
const withoutFeature = (criteria: MatrixCriterion[], featureId: string, categoryRemoved: boolean) =>
  criteria.map((criterion) => ({
    ...criterion,
    categories: criterion.categories
      .filter((category) => !(categoryRemoved && category.features.some((feature) => feature.id === featureId)))
      .map((category) => ({ ...category, features: category.features.filter((feature) => feature.id !== featureId) }))
  }));

const CHANGE_FAILED = "Couldn't save that change. Please try again.";
const ADD_TARGET_GONE = 'That was removed elsewhere, so the matrix was reloaded.';

const fetchCriteria = async (engagementId: string) => {
  const { data, error } = await fetchAdviseFunctionalityMatrix(engagementId);
  return { data: data?.criteria ?? null, error };
};

/** The engagement's Functionality Matrix, loaded when its tab asks, with each change saved in the background. */
export const useEngagementFunctionalityMatrix = (
  engagementId: string | null,
  canEdit: boolean,
  { debouncedSave, flushSaves, awaitOnFlush }: PendingSaves
): EngagementFunctionalityMatrix => {
  const {
    data: criteria,
    failed,
    refresh,
    showEdit,
    isLoadedFor,
    saveOrReload
  } = useLoadAfterSaves(engagementId, fetchCriteria, flushSaves);

  const updateCriterion = useCallback(
    (criterionId: string, value: MatrixCriterionValue) => {
      if (!canEdit || !engagementId) return;
      showEdit((prev) => withCriterion(prev, criterionId, value));
      debouncedSave(
        `matrix:criterion:${criterionId}`,
        saveOrReload(() => updateAdviseMatrixCriterion(engagementId, criterionId, value))
      );
    },
    [canEdit, engagementId, debouncedSave, saveOrReload, showEdit]
  );

  const selectFeature = useCallback(
    (featureId: string, selected: boolean) => {
      if (!canEdit || !engagementId) return;
      showEdit((prev) => withFeature(prev, featureId, selected));
      debouncedSave(
        `matrix:feature:${featureId}`,
        saveOrReload(() => updateAdviseMatrixFeature(engagementId, featureId, selected))
      );
    },
    [canEdit, engagementId, debouncedSave, saveOrReload, showEdit]
  );

  // An add or removal is saved before it shows, and counts as an edit so a load already on its way can't undo it.
  // It resolves to the reason it was refused, or null once saved. A 404 reloads the matrix, since what it names is gone:
  // for a removal that is the outcome asked for, but an add then saved nothing and must say so.
  const saveThenShow = useCallback(
    async <T>(
      request: (id: string) => Promise<CallResult<T>>,
      change: (data: T) => (prev: MatrixCriterion[]) => MatrixCriterion[],
      goneMeans: 'done' | 'refused'
    ): Promise<string | null> => {
      if (!canEdit || !engagementId) return null;
      const call = request(engagementId);
      awaitOnFlush(call);
      const result = await call;
      if (result.error) {
        if (!isNotFoundRefusal(result.error)) {
          return refusalMessage(result.error, CHANGE_FAILED, CHANGE_FAILED);
        }
        refresh();
        return goneMeans === 'done' ? null : ADD_TARGET_GONE;
      }
      if (!isLoadedFor(engagementId)) return null;
      showEdit(change(result.data));
      return null;
    },
    [canEdit, engagementId, awaitOnFlush, refresh, isLoadedFor, showEdit]
  );

  const addCriterion = useCallback(
    (criterion: NewMatrixCriterion) =>
      saveThenShow(
        (id) => createAdviseMatrixCriterion(id, criterion),
        ({ criterion: added }) =>
          (prev) =>
            withCriterionAdded(prev, added),
        'refused'
      ),
    [saveThenShow]
  );

  const addFeature = useCallback(
    (home: MatrixFeatureHome, label: string) =>
      'categoryId' in home
        ? saveThenShow(
            (id) => createAdviseMatrixCategoryFeature(id, home.categoryId, label),
            ({ feature }) =>
              (prev) =>
                withCategoryFeature(prev, home.categoryId, feature),
            'refused'
          )
        : saveThenShow(
            (id) => createAdviseMatrixCriterionFeature(id, home.criterionId, label),
            ({ category }) =>
              (prev) =>
                withCriterionCategory(prev, home.criterionId, category),
            'refused'
          ),
    [saveThenShow]
  );

  const removeCriterion = useCallback(
    (criterionId: string) =>
      saveThenShow(
        (id) => deleteAdviseMatrixCriterion(id, criterionId),
        () => (prev) => prev.filter((criterion) => criterion.id !== criterionId),
        'done'
      ),
    [saveThenShow]
  );

  const removeFeature = useCallback(
    (featureId: string) =>
      saveThenShow(
        (id) => deleteAdviseMatrixFeature(id, featureId),
        ({ category_removed }) =>
          (prev) =>
            withoutFeature(prev, featureId, category_removed),
        'done'
      ),
    [saveThenShow]
  );

  return useMemo(
    () => ({
      criteria,
      failed,
      refresh,
      updateCriterion,
      selectFeature,
      addCriterion,
      addFeature,
      removeCriterion,
      removeFeature
    }),
    [
      criteria,
      failed,
      refresh,
      updateCriterion,
      selectFeature,
      addCriterion,
      addFeature,
      removeCriterion,
      removeFeature
    ]
  );
};
